import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Inline "Back" button placed near a page's title.
 *
 * - `to` is mandatory and must be an explicit route. We avoid `navigate(-1)`
 *   so refreshes and direct-link landings still resolve to a sensible place.
 * - Optional `confirmMessage` shows a `window.confirm` before navigating —
 *   used for risky exits like the in-progress test runner.
 * - Optional `onBeforeNavigate` (sync) lets the caller short-circuit (return
 *   `false`) or run side effects (e.g. autosave) before we leave.
 *
 * The visual style is intentionally quiet: ghost button, stone palette,
 * small size. Always emits `data-testid="back-button"` so tests/automation
 * can drive it.
 */
export default function BackButton({
  to,
  label = "Back",
  confirmMessage,
  onBeforeNavigate,
  className = "",
  testId = "back-button",
}) {
  const navigate = useNavigate();

  const go = () => {
    if (typeof onBeforeNavigate === "function") {
      const proceed = onBeforeNavigate();
      if (proceed === false) return;
    }
    if (confirmMessage) {
      const ok = window.confirm(confirmMessage);
      if (!ok) return;
    }
    navigate(to);
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={go}
      data-testid={testId}
      className={`-ml-2 h-8 px-2 text-stone-600 hover:text-stone-900 hover:bg-stone-100 ${className}`}
    >
      <ArrowLeft className="mr-1.5 h-4 w-4" />
      {label}
    </Button>
  );
}
